import { useMemo, Fragment } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnSizingState,
} from '@tanstack/react-table'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDashboardStore } from '../stores/dashboardStore'
import { useDashboardTable } from '../hooks/useDashboardTable'
import { useColumnPrefsStore } from '../stores/columnPrefsStore'
import { useEmployees } from '../hooks/useEmployees'
import { useOrderTemplates } from '../hooks/useOrderTemplates'
import { StatusBadge } from './StatusBadge'
import { OrderTableExpandedRow } from './OrderTableExpandedRow'
import { TablePagination } from './TablePagination'
import { ColumnFilterPopover } from './ColumnFilterPopover'
import { formatOrderDate } from '../utils/formatters'
import type { DashboardTableOrder } from '../types/dashboard.types'

// Map column IDs to their filter type for header rendering
const COLUMN_FILTERS: Record<string, React.ComponentProps<typeof ColumnFilterPopover>['filterType']> = {
  order_id: { kind: 'range', column: 'orderId' },
  app_status: { kind: 'dropdown', column: 'status' },
  employee_id: { kind: 'dropdown', column: 'employee' },
  order_template_id: { kind: 'dropdown', column: 'template' },
  order_date: { kind: 'date', column: 'orderDate' },
  delivery_date: { kind: 'date', column: 'deliveryDate' },
  customer_name: { kind: 'text', column: 'customerName' },
  reference: { kind: 'text', column: 'reference' },
  your_ref: { kind: 'text', column: 'yourRef' },
  info: { kind: 'text', column: 'info' },
  customer_phone: { kind: 'text', column: 'phone' },
  email: { kind: 'text', column: 'email' },
  customer_id: { kind: 'text', column: 'customerId' },
  delivery_name: { kind: 'text', column: 'deliveryName' },
}

export function OrderTable() {
  const { data: page, isLoading, isError, refetch } = useDashboardTable()
  const { data: employees = [] } = useEmployees()
  const { data: templates = [] } = useOrderTemplates()

  const searchQuery = useDashboardStore((s) => s.searchQuery)
  const selectedOrderId = useDashboardStore((s) => s.selectedOrderId)
  const selectOrder = useDashboardStore((s) => s.selectOrder)
  const sortColumn = useDashboardStore((s) => s.sortColumn)
  const sortDirection = useDashboardStore((s) => s.sortDirection)
  const setSorting = useDashboardStore((s) => s.setSorting)
  const visibleColumns = useColumnPrefsStore((s) => s.visibleColumns)
  const columnSizing = useColumnPrefsStore((s) => s.columnSizing)
  const setColumnSizing = useColumnPrefsStore((s) => s.setColumnSizing)

  const rows = page?.rows ?? []
  const hasNextPage = page?.hasNextPage ?? false

  const employeeMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const e of employees) {
      map.set(e.employee_id, e.full_name)
    }
    return map
  }, [employees])

  const templateMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const t of templates) {
      map.set(t.order_template_id, t.name)
    }
    return map
  }, [templates])

  const allColumns = useMemo<ColumnDef<DashboardTableOrder>[]>(
    () => [
      {
        id: 'expander',
        header: '#',
        size: 44,
        cell: ({ row }) => {
          const pageIndex = useDashboardStore.getState().pageIndex
          const pageSize = useDashboardStore.getState().pageSize
          const rowNum = pageIndex * pageSize + row.index + 1
          return (
            <span className="text-[11px] text-gray-400 tabular-nums select-none">
              {rowNum}
            </span>
          )
        },
      },
      {
        id: 'order_id',
        accessorKey: 'order_id',
        header: 'Ordrenr',
        size: 90,
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">
            #{getValue<number>()}
          </span>
        ),
      },
      {
        id: 'customer_name',
        accessorKey: 'customer_name',
        header: 'Kunde',
        size: 180,
        cell: ({ getValue }) => (
          <span className="truncate block">
            {getValue<string | null>() ?? 'Ukjent kunde'}
          </span>
        ),
      },
      {
        id: 'reference',
        accessorKey: 'reference',
        header: 'Merking',
        size: 120,
        cell: ({ getValue }) => (
          <span className="truncate block text-gray-600">
            {getValue<string | null>() ?? ''}
          </span>
        ),
      },
      {
        id: 'app_status',
        accessorKey: 'app_status',
        header: 'Status',
        size: 100,
        cell: ({ getValue }) => <StatusBadge status={getValue<string | null>()} />,
      },
      {
        id: 'employee_id',
        accessorKey: 'employee_id',
        header: 'Ansatt',
        size: 120,
        cell: ({ getValue }) => {
          const id = getValue<number | null>()
          return (
            <span className="truncate block text-gray-600">
              {id ? employeeMap.get(id) ?? 'Tidligere ansatt' : ''}
            </span>
          )
        },
      },
      {
        id: 'order_date',
        accessorKey: 'order_date',
        header: 'Dato',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-gray-500 whitespace-nowrap">
            {formatOrderDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: 'customer_phone',
        header: 'Tlf',
        size: 120,
        enableSorting: false,
        cell: ({ row }) => {
          const values = [row.original.delivery_phone, row.original.customer_phone]
          const phones = [...new Set(values.filter((v): v is string => !!v && !/@/.test(v)))]
          if (phones.length === 0) return null
          return (
            <span className="truncate block">
              {phones.map((p, i) => (
                <span key={p}>
                  {i > 0 && <span className="text-gray-300"> / </span>}
                  <a href={`tel:${p}`} className="text-emerald-600 hover:underline" onClick={(e) => e.stopPropagation()}>{p}</a>
                </span>
              ))}
            </span>
          )
        },
      },
      {
        id: 'email',
        header: 'E-post',
        size: 160,
        enableSorting: false,
        cell: ({ row }) => {
          const values = [row.original.customer_phone, row.original.delivery_phone]
          const emails = [...new Set(values.filter((v): v is string => !!v && /@/.test(v)))]
          if (emails.length === 0) return null
          return (
            <span className="truncate block">
              {emails.map((e, i) => (
                <span key={e}>
                  {i > 0 && <span className="text-gray-300"> / </span>}
                  <a href={`mailto:${e}`} className="text-emerald-600 hover:underline" onClick={(ev) => ev.stopPropagation()}>{e}</a>
                </span>
              ))}
            </span>
          )
        },
      },
      {
        id: 'delivery_address',
        accessorKey: 'delivery_address',
        header: 'Adresse',
        size: 160,
        cell: ({ getValue }) => (
          <span className="truncate block text-gray-600">
            {getValue<string | null>() ?? ''}
          </span>
        ),
      },
      {
        id: 'order_template_id',
        accessorKey: 'order_template_id',
        header: 'Ordremal',
        size: 100,
        cell: ({ getValue }) => {
          const id = getValue<number | null>()
          return (
            <span className="truncate block text-gray-500">
              {id ? templateMap.get(id) ?? `#${id}` : ''}
            </span>
          )
        },
      },
      {
        id: 'invoice_no',
        accessorKey: 'invoice_no',
        header: 'Fakturanr',
        size: 90,
        cell: ({ getValue }) => (
          <span className="text-gray-500">{getValue<number | null>() ?? ''}</span>
        ),
      },
      {
        id: 'delivery_date',
        accessorKey: 'delivery_date',
        header: 'Lev.dato',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-gray-500 whitespace-nowrap">
            {getValue<string | null>() ? formatOrderDate(getValue<string>()) : ''}
          </span>
        ),
      },
      {
        id: 'your_ref',
        accessorKey: 'your_ref',
        header: 'Kundens ref',
        size: 120,
        cell: ({ getValue }) => (
          <span className="truncate block text-gray-600">
            {getValue<string | null>() ?? ''}
          </span>
        ),
      },
      {
        id: 'info',
        accessorKey: 'info',
        header: 'Ordreinfo',
        size: 160,
        cell: ({ getValue }) => (
          <span className="truncate block text-gray-500 text-[11px]">
            {getValue<string | null>() ?? ''}
          </span>
        ),
      },
      {
        id: 'line_count',
        accessorKey: 'line_count',
        header: 'Linjer',
        size: 60,
        cell: ({ getValue }) => (
          <span className="text-gray-500 tabular-nums">{getValue<number>()}</span>
        ),
      },
      {
        id: 'order_total',
        accessorKey: 'order_total',
        header: 'Total eks. mva',
        size: 120,
        cell: ({ getValue }) => {
          const netto = getValue<number | null>()
          if (netto == null) return null
          return (
            <span className={`tabular-nums text-right block ${netto < 0 ? 'text-red-500' : 'text-gray-600'}`}>
              {netto.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )
        },
      },
      {
        id: 'customer_id',
        accessorKey: 'customer_id',
        header: 'Kundenr',
        size: 80,
        cell: ({ getValue }) => (
          <span className="text-gray-500 tabular-nums">{getValue<number | null>() ?? ''}</span>
        ),
      },
      {
        id: 'delivery_name',
        accessorKey: 'delivery_name',
        header: 'Lev.navn',
        size: 140,
        cell: ({ getValue }) => (
          <span className="truncate block text-gray-600">
            {getValue<string | null>() ?? ''}
          </span>
        ),
      },
    ],
    [employeeMap, templateMap]
  )

  const columns = useMemo(() => {
    const expander = allColumns.find((col) => col.id === 'expander')!
    const ordered = visibleColumns
      .map((id) => allColumns.find((col) => col.id === id))
      .filter(Boolean) as ColumnDef<DashboardTableOrder>[]
    return [expander, ...ordered]
  }, [allColumns, visibleColumns])

  const sorting = useMemo<SortingState>(
    () => [{ id: sortColumn, desc: sortDirection === 'desc' }],
    [sortColumn, sortDirection]
  )

  // Accepted exception, not debt: TanStack Table's `useReactTable()` returns
  // functions the React Compiler cannot memoise, so it skips compiling this
  // component. That is the library's design — the table instance is meant to be
  // read fresh every render — and there is no alternative API to reach for.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnSizing: columnSizing as ColumnSizingState },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater
      if (newSorting.length > 0) {
        setSorting(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc')
      }
    },
    onColumnSizingChange: (updater) => {
      const current = columnSizing as ColumnSizingState
      const next = typeof updater === 'function' ? updater(current) : updater
      setColumnSizing(next)
    },
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    // Ingen pageCount: totalen finnes ikke lenger på standardveien (#934).
    // Paginering er første/forrige/neste, drevet av sentinel-raden.
    getRowCanExpand: () => true,
  })

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-sm text-gray-400">Kunne ikke laste ordrer</p>
        <button
          onClick={() => refetch()}
          className="text-sm text-emerald-600 hover:underline"
        >
          Prøv igjen
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
        <div className="flex-1 overflow-auto">
          <table className="text-[13px]" style={{ width: table.getTotalSize(), tableLayout: 'fixed' }}>
            <thead className="sticky top-0 bg-gray-50/80 backdrop-blur-sm z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-100">
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort() && header.id !== 'expander'
                    const sorted = header.column.getIsSorted()
                    const filterType = COLUMN_FILTERS[header.id]
                    return (
                      <th
                        key={header.id}
                        className={cn(
                          'group relative px-4 py-2.5 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap border-r border-gray-200 last:border-r-0',
                          canSort && 'cursor-pointer select-none hover:text-gray-600'
                        )}
                        style={{ width: header.getSize() }}
                      >
                        <span className="inline-flex items-center gap-1">
                          <span
                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </span>
                          {sorted === 'asc' && <ArrowUp size={12} />}
                          {sorted === 'desc' && <ArrowDown size={12} />}
                          {filterType && <ColumnFilterPopover filterType={filterType} />}
                        </span>
                        {header.id !== 'expander' && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn(
                              'absolute right-0 top-0 h-full w-1 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity',
                              header.column.getIsResizing() ? 'bg-emerald-500 opacity-100' : 'bg-gray-300'
                            )}
                          />
                        )}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 animate-pulse">
                    {columns.map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-16 text-center text-sm text-gray-400"
                  >
                    {searchQuery ? `Ingen treff for «${searchQuery}»` : 'Ingen ordrer funnet'}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr
                      onClick={() => {
                        selectOrder(row.original.order_id)
                        // Collapse all other rows, toggle this one
                        table.getRowModel().rows.forEach((r) => {
                          if (r.id !== row.id && r.getIsExpanded()) r.toggleExpanded()
                        })
                        row.toggleExpanded()
                      }}
                      className={cn(
                        'border-b border-gray-50 cursor-pointer transition-colors',
                        selectedOrderId === row.original.order_id
                          ? 'bg-emerald-50/60 border-l-2 border-l-emerald-500'
                          : 'hover:bg-gray-50/50'
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 overflow-hidden border-r border-gray-100 last:border-r-0"
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                    {row.getIsExpanded() && (
                      <OrderTableExpandedRow
                        orderId={row.original.order_id}
                        colSpan={row.getVisibleCells().length}
                      />
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePagination rowCount={rows.length} hasNextPage={hasNextPage} />
    </div>
  )
}
